import React, {useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {MenuItem, Tooltip, Typography} from '@mui/material';
import {OcrBatchIdResponse} from '../../../../../models/ocr.models';
import {
    ActiveOptionsButton,
    OcrBatchItemWrapper,
    OptionsButton,
    StyledButton,
    StyledInput
} from "./OcrBatchItem.styled";
import {ReactComponent as TrashSvg} from "../../../../../assets/icons/trash.svg";
import {ReactComponent as EditSvg} from "../../../../../assets/icons/edit.svg";
import {ReactComponent as PinSvg} from "../../../../../assets/icons/pin.svg";
import {ReactComponent as MoreSvg} from "../../../../../assets/icons/more.svg";
import {useAppConstants} from "../../../../../config/appConstants";
import {useTranslation} from "react-i18next";
import {useSnackbar} from "../../../../../contexts/SnackbarContext";
import {useOcrBatch} from "../../../../../contexts/OcrBatchContext";
import {StyledMenu} from "../../Sidebar.styled";
import {formatDateWithLanguage, isISODateString} from "../../../../../utils/format-date";
import i18n from "i18next";

interface OcrBatchItemProps {
    batch: OcrBatchIdResponse;
    isOpen: boolean;
    currentBatchId: number | null;
}

const OcrBatchItem = ({ currentBatchId, batch, isOpen }: OcrBatchItemProps) => {
    const {DEFAULT_OCR_BATCH_TITLE_PREFIX} = useAppConstants();
    const {t} = useTranslation();

    const navigate = useNavigate();
    const {showSnackbar} = useSnackbar();

    const {patchOcrBatchTitle, deleteOcrBatch, pinOcrBatch, unpinOcrBatch} = useOcrBatch();
    const inputRef = useRef<HTMLInputElement>(null);
    const isSavingRef = useRef<boolean>(false);

    const [isEditing, setIsEditing] = useState(false);
    const [editTitle, setEditTitle] = useState('');
    const [menuAnchorEl, setMenuAnchorEl] = useState<null | HTMLElement>(null);

    const {id, title, createdAt, pinned} = batch;
    const isActive = currentBatchId === id;
    const timestamp = formatDateWithLanguage(createdAt, i18n.language);
    const defaultTitle = `${DEFAULT_OCR_BATCH_TITLE_PREFIX}${timestamp}`;
    const displayTitle = (!title || isISODateString(title)) ? defaultTitle : title;

    const handleOpenOcrBatchDetails = () => {
        if (isEditing) return;
        navigate(`/ocr/batch/${id}`);
    };

    const handleOpenActionMenu = (e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        setMenuAnchorEl(e.currentTarget);
    };

    const handleCloseMenu = () => setMenuAnchorEl(null);

    const handleTogglePin = async () => {
        handleCloseMenu();

        try {
            if (pinned) {
                await unpinOcrBatch(id);
            } else {
                await pinOcrBatch(id);
            }
        } catch (error) {
            showSnackbar(t('sidebar.ocrBatchItem.pinUnpinBatchErrorMessage'), 'error');
        }
    };

    const handleDeleteBatch = async (e: React.MouseEvent<HTMLElement>) => {
        e.stopPropagation();
        handleCloseMenu();
        try {
            await deleteOcrBatch(id);
            showSnackbar(t('sidebar.ocrBatchItem.batchDeletedSuccessMessage'), 'success');
        } catch (error) {
            showSnackbar(t('sidebar.ocrBatchItem.batchDeletedErrorMessage'), 'error');
        }
    };

    const handleOpenTitleEdition = () => {
        setEditTitle(batch.title || displayTitle);
        setIsEditing(true);
    };

    const handleInputTouched = () => {
        setTimeout(async () => {
            if (!menuAnchorEl) {
                await handleTitleUpdate();
            }
        }, 150);
    };

    const handleTitleUpdate = async () => {
        if (isSavingRef.current) return;

        if (!editTitle.trim()) {
            setIsEditing(false);
            return;
        }

        if (menuAnchorEl) handleCloseMenu();

        isSavingRef.current = true;

        try {
            await patchOcrBatchTitle(id, editTitle.trim());
            setIsEditing(false);
        } catch (error) {
            showSnackbar(t('sidebar.ocrBatchItem.batchTitleUpdateErrorMessage'), 'error');
        } finally {
            isSavingRef.current = false;
        }
    };

    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    return (
        <OcrBatchItemWrapper $active={isActive}>
            <StyledButton
                open={isOpen}
                onClick={handleOpenOcrBatchDetails}
                onDoubleClick={handleOpenTitleEdition}
            >
                {isEditing ? (
                    <StyledInput
                        ref={inputRef}
                        type="text"
                        value={editTitle}
                        onChange={(e) => setEditTitle(e.target.value)}
                        onBlur={handleInputTouched}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleTitleUpdate();
                            if (e.key === 'Escape') {
                                setIsEditing(false);
                                setEditTitle('');
                            }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        onMouseDown={(e) => e.stopPropagation()}
                        aria-label={t('sidebar.ocrBatchItem.editTitle')}
                    />
                ) : (
                    <>
                        {pinned && (
                            <div
                                style={{
                                    marginRight: '4px',
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                <PinSvg
                                    style={{
                                        width: '16px',
                                        height: '16px',
                                    }}
                                />
                            </div>
                        )}
                        <Typography variant='body2' component='span'>
                            {displayTitle}
                        </Typography>
                    </>
                )}
            </StyledButton>

            <Tooltip title={t('sidebar.ocrBatchItem.showOptions')}>
                {isActive ? (
                    <ActiveOptionsButton onClick={handleOpenActionMenu} aria-label={t('sidebar.ocrBatchItem.showOptions')}>
                        <MoreSvg />
                    </ActiveOptionsButton>
                ) : (
                    <OptionsButton onClick={handleOpenActionMenu} aria-label={t('sidebar.ocrBatchItem.showOptions')}>
                        <MoreSvg />
                    </OptionsButton>
                )}
            </Tooltip>

            <StyledMenu
                anchorEl={menuAnchorEl}
                open={Boolean(menuAnchorEl)}
                onClose={handleCloseMenu}
            >
                <MenuItem onClick={handleTogglePin}>
                    <PinSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>
                    <span style={{color: '#474747', fontSize: '14px'}}>
                        {pinned ? t('sidebar.unpin') : t('sidebar.pin')}
                    </span>
                </MenuItem>

                <MenuItem onClick={() => {
                    handleCloseMenu();
                    handleOpenTitleEdition();
                }}>
                    <EditSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>
                    <span style={{color: '#474747', fontSize: '14px'}}>{t('sidebar.changeName')}</span>
                </MenuItem>

                <MenuItem onClick={handleDeleteBatch}>
                    <TrashSvg style={{marginRight: '14px', width: '19px', height: '19px', color: '#474747'}}/>
                    <span style={{color: '#474747', fontSize: '14px'}}>{t('sidebar.delete')}</span>
                </MenuItem>
            </StyledMenu>
        </OcrBatchItemWrapper>
    );
};

export default OcrBatchItem;